import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Star,
  MapPin,
  Camera,
  Heart,
  Share2,
  CheckCircle,
  Zap,
  Crown,
  X,
  // Package,
} from "lucide-react";
import { Button } from "../../components/shared";
import {
  OverviewTab,
  ServicesTab,
  ReviewsTab,
  ContactTab,
} from "../../components/client/service";
import { useVendorService } from "../../services/vendorService";
import ServiceDetailSkeleton from "../../components/shared/ServiceDetailSkeleton";
import { useAuth } from "../../contexts/AuthContext";
import useShare from "../../hooks/useShare";
import BookingWidget from "../../components/client/BookingWidget";

const ClientVendorServices = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [isFavorited, setIsFavorited] = useState(false);
  const [showMobileBooking, setShowMobileBooking] = useState(false);

  // Fetch vendor service data from API
  const { data: serviceResponse, isLoading, error } = useVendorService(id);

  // console.log('Service response:', serviceResponse);
  // Transform API data to component format
  const service = serviceResponse?.data;
  const vendor = service?.vendor_profile;
  const category = service?.service_category;

  // Service-specific ratings (using the new VendorService rating methods)
  const serviceRating = service?.average_rating || 0;
  const serviceReviews = service?.total_reviews || 0;
  const serviceRatingStats = service?.rating_stats || {
    total_reviews: 0,
    average_rating: 0,
    five_star: 0,
    four_star: 0,
    three_star: 0,
    two_star: 0,
    one_star: 0,
  };

  // Debug logging
  // console.log("Service response:", serviceResponse);
  // console.log("Service data:", service);
  // console.log("Vendor service packages:", service?.vendor_service_packages);

  // Check if service has packages
  const hasPackages = service?.vendor_service_packages?.length > 0;

  // Calculate starting price from packages (minimum price) or fallback to service base_price
  const calculateStartingPrice = () => {
    if (hasPackages && service?.vendor_service_packages?.length > 0) {
      const prices = service.vendor_service_packages
        .map((pkg) => parseFloat(pkg.base_price || 0))
        .filter((price) => price > 0);

      if (prices.length > 0) {
        return Math.min(...prices);
      }
    }
    return parseFloat(service?.base_price || 0);
  };

  const startingPrice = calculateStartingPrice();

  // Format price for display
  const formatPrice = (price) => {
    if (!price || price === 0) return "Contact";
    return price.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Share functionality hook
  const { handleShare } = useShare({
    title: service?.service_name || "Service",
    text: `Check out ${service?.service_name}${
      vendor?.business_name ? ` by ${vendor.business_name}` : ""
    } on PhotoBooth`,
  });

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "services", label: "Services & Pricing" },
    { id: "reviews", label: "Reviews" },
    { id: "contact", label: "Contact" },
  ];

  if (isLoading) {
    return <ServiceDetailSkeleton />;
  }

  if (error || !service) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-light text-black mb-2">
            Service Not Found
          </h2>
          <p className="text-gray-600 mb-4 font-light">
            The service you're looking for doesn't exist or is no longer
            available.
          </p>
          <Link
            to="/browse"
            className="inline-block px-6 py-3 bg-black text-white rounded-lg font-light hover:bg-gray-800 transition-colors"
          >
            Browse Services
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Service Name - Top */}
        <h1 className="text-2xl sm:text-3xl lg:text-3xl font-light text-black mb-6 leading-tight">
          {service?.service_name}
        </h1>

        {/* Main Content Grid - Airbnb Style */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-8 lg:gap-12">
          {/* Left Column - Main Content */}
          <div className="space-y-8">
            {/* Service Image */}
            <div className="relative">
              <div className="aspect-[4/3] sm:aspect-[16/10] bg-gray-100 rounded-lg overflow-hidden">
                {service?.image_url ||
                vendor?.image_url ||
                vendor?.user?.profile_image_url ? (
                  <img
                    src={
                      service?.image_url ||
                      vendor?.image_url ||
                      vendor?.user?.profile_image_url
                    }
                    alt={service?.service_name || "Service image"}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 font-light">
                        Service Preview
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {/* Badges */}
              <div className="absolute top-4 left-4 flex flex-col gap-2">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-light bg-white border border-gray-200 text-black shadow-sm">
                  {category?.category_name || "Premium Service"}
                </span>
                {service?.is_featured && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-light bg-black text-white shadow-sm">
                    <Crown className="w-3 h-3 mr-1" />
                    Featured
                  </span>
                )}
              </div>
            </div>

            {/* Location, Rating, Vendor - After Image */}
            <div className="pb-6 border-b border-gray-200">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {vendor?.is_verified && (
                  <div className="flex items-center text-black px-2 py-1 rounded-full border border-gray-200">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    <span className="text-xs font-light">Verified Vendor</span>
                  </div>
                )}
                <div className="flex items-center text-gray-600">
                  <MapPin className="w-3 h-3 mr-1" />
                  <span className="text-xs font-light">
                    {vendor
                      ? `${vendor.city}, ${vendor.state}`
                      : "Location not available"}
                  </span>
                </div>
              </div>

              {/* Rating and Action Buttons - Same Row */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4">
                <div className="flex items-center gap-1">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((star) => {
                      const isFilled = star <= Math.floor(serviceRating);
                      const hasPartial =
                        star === Math.ceil(serviceRating) &&
                        serviceRating % 1 !== 0;
                      return (
                        <Star
                          key={star}
                          className={`w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 transition-all duration-500 ease-out cursor-default animate-in fade-in zoom-in ${
                            isFilled
                              ? "text-yellow-400 fill-yellow-400 hover:scale-125 hover:drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]"
                              : hasPartial
                              ? "text-yellow-400 fill-yellow-400 fill-opacity-50 hover:scale-125 hover:drop-shadow-[0_0_8px_rgba(251,191,36,0.8)]"
                              : "text-gray-300 hover:text-gray-400 hover:scale-110"
                          }`}
                          style={{
                            animationDelay: `${star * 80}ms`,
                            animationFillMode: "both",
                          }}
                        />
                      );
                    })}
                  </div>
                  <span className="text-xs sm:text-sm md:text-base font-light ml-1 text-black transition-all duration-300">
                    {serviceRating.toFixed(1)}
                  </span>
                  <span className="text-gray-500 ml-1 text-xs sm:text-sm font-light">
                    ({serviceReviews} reviews)
                  </span>
                </div>

                {/* Action Buttons - Far Right */}
                <div className="flex items-center gap-3 sm:gap-4">
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-1.5 sm:gap-2 text-black underline font-light hover:no-underline transition-all text-xs sm:text-sm"
                    aria-label="Share service"
                  >
                    <Share2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>Share</span>
                  </button>
                  {/* <button
                    onClick={() => setIsFavorited(!isFavorited)}
                    className="flex items-center gap-1.5 sm:gap-2 text-black underline font-light hover:no-underline transition-all text-xs sm:text-sm"
                  >
                    <Heart
                      className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                        isFavorited ? "fill-current" : ""
                      }`}
                    />
                    <span>Save</span>
                  </button> */}
                </div>
              </div>

              {/* By Vendor - Below Rating Row */}
              <div className="mb-4">
                <p className="text-sm sm:text-base text-gray-600 font-light">
                  by{" "}
                  {vendor?.user_id || vendor?.user?.id ? (
                    <Link
                      to={`/vendor/${vendor?.id}`}
                      className="font-medium text-black underline hover:no-underline transition-all"
                    >
                      {vendor?.business_name}
                    </Link>
                  ) : (
                    <span className="font-medium text-black">
                      {vendor?.business_name}
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Description */}
            <div className="pb-6 border-b border-gray-200">
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed font-light whitespace-pre-line">
                {service?.description ||
                  "Experience exceptional service with our professional team. We deliver quality and excellence in every project."}
              </p>
            </div>

            {/* Key Details */}
            {/* <div className="pb-6 border-b border-gray-200">
              <h2 className="text-lg sm:text-xl font-light text-black mb-6">
                What this service offers
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-black" />
                    <span className="text-sm font-light text-gray-600">
                      Starting Price
                    </span>
                  </div>
                  <div className="text-lg font-light text-black">
                    ${formatPrice(startingPrice)}
                      </div>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Clock className="w-5 h-5 text-black" />
                    <span className="text-sm font-light text-gray-600">
                      Duration
                    </span>
                  </div>
                  <div className="text-lg font-light text-black">
                    {service?.min_duration_hours
                      ? `${service.min_duration_hours}-${
                          service.max_duration_hours ||
                          service.min_duration_hours
                        } hours`
                      : "Flexible"}
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <Award className="w-5 h-5 text-black" />
                    <span className="text-sm font-light text-gray-600">
                      Pricing Type
                    </span>
                  </div>
                  <div className="text-lg font-light text-black capitalize">
                    {service?.price_type || "Fixed"}
            </div>
          </div>
        </div>
            </div> */}

            {/* Tabs Navigation */}
            <div className="border-b border-gray-200 mb-6">
              <nav className="flex overflow-x-auto -mb-px">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative py-4 px-4 sm:px-6 font-light text-sm sm:text-base transition-all duration-300 whitespace-nowrap border-b-2 ${
                      activeTab === tab.id
                        ? "text-black border-black"
                        : "text-gray-600 border-transparent hover:text-black hover:border-gray-300"
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === "overview" && (
                <OverviewTab
                  service={service}
                  vendor={vendor}
                  serviceRating={serviceRating}
                  serviceReviews={serviceReviews}
                  formatPrice={formatPrice}
                  startingPrice={startingPrice}
                />
              )}

              {activeTab === "services" && <ServicesTab service={service} />}

              {activeTab === "reviews" && (
                <ReviewsTab
                  serviceRating={serviceRating}
                  serviceReviews={serviceReviews}
                  serviceRatingStats={serviceRatingStats}
                  vendorId={vendor?.id || vendor?.user_id}
                />
              )}

              {activeTab === "contact" && <ContactTab />}
            </div>
          </div>

          {/* Right Column - Sticky Booking Widget (Desktop Only) */}
          <div className="hidden lg:block">
            <div className="sticky top-20">
              <BookingWidget
                service={service}
                vendor={vendor}
                category={category}
                startingPrice={startingPrice}
                hasPackages={hasPackages}
                serviceRating={serviceRating}
                serviceReviews={serviceReviews}
                isAuthenticated={isAuthenticated}
                navigate={navigate}
                formatPrice={formatPrice}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Booking Button - Fixed Bottom */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-light text-black">
                  ${formatPrice(startingPrice)}
                </span>
                <span className="text-sm font-light text-gray-600">
                  starting price
                </span>
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              className="!rounded-lg !px-6 !py-2 text-sm font-light !bg-black hover:!bg-gray-800 !text-white"
              icon={Zap}
              onClick={() => setShowMobileBooking(true)}
            >
              Book
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Booking Modal */}
      {showMobileBooking && (
        <div className="lg:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileBooking(false)}
          />

          {/* Modal Content */}
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between z-10">
              <h3 className="text-lg font-light text-black">Booking Details</h3>
              <button
                onClick={() => setShowMobileBooking(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="p-4">
              <BookingWidget
                service={service}
                vendor={vendor}
                category={category}
                startingPrice={startingPrice}
                hasPackages={hasPackages}
                serviceRating={serviceRating}
                serviceReviews={serviceReviews}
                isAuthenticated={isAuthenticated}
                navigate={navigate}
                formatPrice={formatPrice}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientVendorServices;
